import { ReactNode } from 'react';
import {
  ListItem,
  ListItemAvatar,
  Box,
  Avatar,
  AvatarProps,
  Typography,
} from '@mui/material';
import { Membership } from 'shared/types';
import { UserStatusChip } from 'components/UserStatusChip';
import { LegendIcon } from './LegendIcon';
import { LegendListItemText } from './LegendListItemText';
import {
  getLegendPointAvatarBackgroundStyles,
  withLegendPointAvatarBackground,
} from './LegendDrawerContent.utils';

type LegendListItemProps =
  | {
      primaryText: string;
      secondary: ReactNode | string;
      mapLayer?: string;
      iconSrc?: string;
      iconSize?: number;
      minMembership?: Membership;
      avatarSxProps?: AvatarProps['sx'];
    }
  | {
      primaryText: string;
      secondary: ReactNode | string;
      mapLayer?: string;
      iconColor?: string;
    }
  | {
      primaryText: string;
      secondary: ReactNode | string;
      AvatarComponent?: ReactNode;
      listItemAvatarSxProps?: AvatarProps['sx'];
    };

export const LegendListItem = (props: LegendListItemProps): JSX.Element => {
  const { primaryText, secondary } = props;

  if ('iconSrc' in props) {
    const {
      iconSrc,
      iconSize,
      minMembership = 'public',
      avatarSxProps,
    } = props;
    return (
      <ListItem alignItems="flex-start">
        {iconSrc && (
          <ListItemAvatar sx={{ mt: 0.5 }}>
            <Avatar sx={withLegendPointAvatarBackground(avatarSxProps)}>
              <LegendIcon src={iconSrc} alt={primaryText} size={iconSize} />
            </Avatar>
          </ListItemAvatar>
        )}
        <LegendListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: 'fontWeightBold' }}
              >
                {primaryText}
              </Typography>
              {minMembership !== 'public' && (
                <UserStatusChip
                  type={minMembership === 'pro' ? 'pro' : 'members'}
                  sx={{ marginLeft: 0.5 }}
                />
              )}
            </Box>
          }
          secondary={secondary}
          variant="watchduty-legend"
        />
      </ListItem>
    );
  }

  if ('iconColor' in props) {
    const { iconColor } = props;
    return (
      <ListItem alignItems="flex-start">
        {iconColor && (
          <ListItemAvatar>
            <Avatar sx={(theme) => getLegendPointAvatarBackgroundStyles(theme)}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 24,
                  backgroundColor: iconColor,
                }}
              />
            </Avatar>
          </ListItemAvatar>
        )}
        <LegendListItemText
          primary={primaryText}
          secondary={secondary}
          variant="watchduty-legend"
        />
      </ListItem>
    );
  }

  if ('AvatarComponent' in props) {
    const { AvatarComponent, listItemAvatarSxProps } = props;
    return (
      <ListItem alignItems="flex-start">
        {AvatarComponent && (
          <ListItemAvatar sx={listItemAvatarSxProps}>
            {AvatarComponent}
          </ListItemAvatar>
        )}
        <LegendListItemText
          primary={primaryText}
          secondary={secondary}
          variant="watchduty-legend"
        />
      </ListItem>
    );
  }

  return (
    <ListItem alignItems="flex-start">
      <LegendListItemText
        primary={primaryText}
        secondary={secondary}
        variant="watchduty-legend"
      />
    </ListItem>
  );
};
